import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { InstitutionBrandingDto } from '@ciimar/shared';
import { api } from '../api/client';

const BrandingContext = createContext<InstitutionBrandingDto | null>(null);

/**
 * Loads the institution's public branding once (name, logo, favicon, login
 * background) and applies the favicon + document title globally. Served before
 * login, so it must not require auth. Falls back to the built-in title.
 */
export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<InstitutionBrandingDto | null>(null);

  useEffect(() => {
    let active = true;
    api
      .institutionBranding()
      .then((b) => active && setBranding(b))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    document.title = branding?.name ? `${branding.name} · Portal` : 'CIIMAR Portal';
    if (branding?.icon) {
      let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = branding.icon;
    }
  }, [branding]);

  return <BrandingContext.Provider value={branding}>{children}</BrandingContext.Provider>;
}

export function useBranding(): InstitutionBrandingDto | null {
  return useContext(BrandingContext);
}
